import { subProductsInitialState } from "../constants/state";
import { emptySubProductTable } from "../constants/templates";
import { getEmptyPropertyFromType } from "../functions/form";
import {
    SubProductAction,
    SubProductActionTypes,
} from "../types/actions.subproduct";
import { SubProductsState } from "../types/state";
import { Property } from "../types/subproduct";

const subProductsReducer = (
    state = subProductsInitialState,
    action: SubProductActionTypes
): SubProductsState => {
    const stateCurrentTable = state.tables[state.shownTableId];

    switch (action.type) {
        case SubProductAction.ASSIGN_PROPERTY:
            const currentColumnIdx = stateCurrentTable.columns.findIndex(
                (propertyColumn) =>
                    propertyColumn.accessor === action.propertyColumn.accessor
            );
            let reassignedColumns = [...stateCurrentTable.columns];

            // don't allow reassigning of fixed property
            if (reassignedColumns[currentColumnIdx]?.isFixed) return state;
            if (currentColumnIdx !== -1) {
                // editing a property
                reassignedColumns[currentColumnIdx] = action.propertyColumn;
            } else {
                // adding a new property
                reassignedColumns = [
                    ...reassignedColumns,
                    action.propertyColumn,
                ];
            }

            return {
                ...state,
                tables: {
                    ...state.tables,
                    [state.shownTableId]: {
                        ...stateCurrentTable,
                        columns: reassignedColumns,
                        subProducts: Object.values(
                            stateCurrentTable.subProducts
                        ).reduce(
                            (acc, subProduct) => ({
                                ...acc,
                                [subProduct.id]: {
                                    ...subProduct,
                                    properties: {
                                        ...subProduct.properties,
                                        [action.propertyColumn.accessor]: {
                                            accessor:
                                                action.propertyColumn.accessor,
                                            type: action.propertyColumn.type,
                                            value:
                                                action.propertyColumn.type ===
                                                subProduct.properties[
                                                    action.propertyColumn
                                                        .accessor
                                                ]?.type
                                                    ? subProduct.properties[
                                                          action.propertyColumn
                                                              .accessor
                                                      ].value
                                                    : getEmptyPropertyFromType(
                                                          action.propertyColumn
                                                              .type
                                                      ),
                                        },
                                    },
                                },
                            }),
                            {}
                        ),
                    },
                },
            };

        case SubProductAction.ASSIGN_SUBPRODUCT:
            const currentRowIdx = stateCurrentTable.rows.findIndex(
                (subProductId) => subProductId === action.subProduct.id
            );
            let reassignedRows = [...stateCurrentTable.rows];
            if (currentRowIdx !== -1) {
                // editing a property
                reassignedRows[currentRowIdx] = action.subProduct.id;
            } else {
                // adding a new property
                reassignedRows = [...reassignedRows, action.subProduct.id];
            }
            return {
                ...state,
                tables: {
                    ...state.tables,
                    [state.shownTableId]: {
                        ...stateCurrentTable,
                        subProducts: {
                            ...stateCurrentTable.subProducts,
                            [action.subProduct.id]: {
                                ...action.subProduct,
                                properties:
                                    currentRowIdx === -1
                                        ? stateCurrentTable.columns.reduce(
                                              (acc, propertyColumn) => ({
                                                  ...acc,
                                                  [propertyColumn.accessor]: {
                                                      accessor:
                                                          propertyColumn.accessor,
                                                      type: propertyColumn.type,
                                                      value: getEmptyPropertyFromType(
                                                          propertyColumn.type
                                                      ),
                                                  },
                                              }),
                                              {}
                                          )
                                        : action.subProduct.properties,
                            },
                        },
                        rows: reassignedRows,
                    },
                },
            };

        case SubProductAction.ADD_TABLE:
            const generatedTableId = Date.now().toString();
            return {
                ...state,
                tableIds: [...state.tableIds, generatedTableId],
                tables: {
                    ...state.tables,
                    [generatedTableId]: {
                        ...emptySubProductTable,
                        id: generatedTableId,
                    },
                },
            };

        case SubProductAction.REMOVE_PROPERTY:
            // don't allow removal of fixed columns / properties
            if (stateCurrentTable.columns[action.idx].isFixed) return state;

            return {
                ...state,
                tables: {
                    ...state.tables,
                    [state.shownTableId]: {
                        ...stateCurrentTable,
                        columns: stateCurrentTable.columns.filter(
                            (propertyColumn) =>
                                propertyColumn.accessor !== action.accessor
                        ),
                        subProducts: Object.values(
                            stateCurrentTable.subProducts
                        ).reduce((acc, subProduct) => {
                            const {
                                [action.accessor]: removedValue,
                                ...remainingProperties
                            } = subProduct.properties;
                            return {
                                ...acc,
                                [subProduct.id]: {
                                    ...subProduct,
                                    properties: remainingProperties,
                                },
                            };
                        }, {}),
                    },
                },
            };

        case SubProductAction.REMOVE_SUBPRODUCT:
            const { [action.id]: removedValue, ...remainingSubProducts } =
                stateCurrentTable.subProducts;
            return {
                ...state,
                tables: {
                    ...state.tables,
                    [state.shownTableId]: {
                        ...stateCurrentTable,
                        rows: stateCurrentTable.rows.filter(
                            (id) => action.id !== id
                        ),
                        subProducts: remainingSubProducts,
                    },
                },
            };

        case SubProductAction.REMOVE_TABLE:
            const { [action.id]: removedTable, ...remainingTables } =
                state.tables;
            return {
                ...state,
                tableIds: state.tableIds.filter((id) => action.id !== id),
                tables: remainingTables,
            };

        case SubProductAction.MOVE_AND_INSERT_SUBPRODUCT:
            // delete from old position in array and insert deleted at new position
            const updatedRows = [...stateCurrentTable.rows];
            const [removedRow] = updatedRows.splice(action.oldIdx, 1);
            updatedRows.splice(action.newIdx, 0, removedRow);

            return {
                ...state,
                tables: {
                    ...state.tables,
                    [state.shownTableId]: {
                        ...stateCurrentTable,
                        rows: updatedRows,
                    },
                },
            };

        case SubProductAction.MOVE_AND_INSERT_PROPERTY:
            // delete from old position in array and insert deleted at new position
            const updatedColumns = [...stateCurrentTable.columns];
            const [removedColumn] = updatedColumns.splice(action.oldIdx, 1);
            updatedColumns.splice(action.newIdx, 0, removedColumn);

            return {
                ...state,
                tables: {
                    ...state.tables,
                    [state.shownTableId]: {
                        ...stateCurrentTable,
                        columns: updatedColumns,
                    },
                },
            };

        case SubProductAction.MOVE_AND_INSERT_TABLE:
            // delete from old position in array and insert deleted at new position
            const updatedTableIds = [...state.tableIds];
            const [removedTableId] = updatedTableIds.splice(action.oldIdx, 1);
            updatedTableIds.splice(action.newIdx, 0, removedTableId);

            return {
                ...state,
                tableIds: updatedTableIds,
            };

        case SubProductAction.RESET_TABLE_COLUMNS:
            return {
                ...state,
                tables: {
                    ...state.tables,
                    [state.shownTableId]: {
                        ...stateCurrentTable,
                        columns: [],
                    },
                },
            };

        case SubProductAction.SET_TABLES:
            return {
                ...state,
                tables: action.tables,
            };

        case SubProductAction.SET_TABLE_IDS:
            return {
                ...state,
                tableIds: action.tableIds,
            };

        case SubProductAction.SET_SHOWN_TABLE_ID:
            return {
                ...state,
                shownTableId: action.id,
            };

        case SubProductAction.SET_TEMP_SHOWN_TABLE:
            return {
                ...state,
                tempShownTable: action.table,
            };

        case SubProductAction.SET_TABLE_NAME:
            return {
                ...state,
                tables: {
                    ...state.tables,
                    [state.shownTableId]: {
                        ...stateCurrentTable,
                        name: action.name,
                    },
                },
            };

        case SubProductAction.SET_TABLE_DATA:
            if (!action.id) return state;

            return {
                ...state,
                tables: {
                    ...state.tables,
                    [action.id]: {
                        ...state.tables[state.shownTableId],
                        ...action.data,
                    },
                },
            };

        case SubProductAction.SET_SUBPRODUCT_PROPERTY_VALUE:
            return {
                ...state,
                tables: {
                    ...state.tables,
                    [state.shownTableId]: {
                        ...stateCurrentTable,
                        subProducts: {
                            ...stateCurrentTable.subProducts,
                            [action.subProductId]: {
                                ...stateCurrentTable.subProducts[
                                    action.subProductId
                                ],
                                properties: {
                                    ...stateCurrentTable.subProducts[
                                        action.subProductId
                                    ].properties,
                                    [action.accessor]: {
                                        ...stateCurrentTable.subProducts[
                                            action.subProductId
                                        ].properties[action.accessor],
                                        value: action.propertyValue,
                                    } as Property,
                                },
                            },
                        },
                    },
                },
            };

        case SubProductAction.SET_SUBPRODUCT_IMAGE_VALUE:
            return {
                ...state,
                tables: {
                    ...state.tables,
                    [state.shownTableId]: {
                        ...stateCurrentTable,
                        subProducts: {
                            ...stateCurrentTable.subProducts,
                            [action.subProductId]: {
                                ...stateCurrentTable.subProducts[
                                    action.subProductId
                                ],
                                image: action.imageUrl ?? "",
                            },
                        },
                    },
                },
            };

        case SubProductAction.SET_GROUPING_MODE:
            return {
                ...state,
                groupingMode: action.groupingMode,
            };

        case SubProductAction.SET_READONLY_MODE:
            return {
                ...state,
                readOnlyMode: action.isReadOnly,
            };

        case SubProductAction.SET_COLUMN_GROUPS:
            return {
                ...state,
                tables: {
                    ...state.tables,
                    [state.shownTableId]: {
                        ...stateCurrentTable,
                        ...stateCurrentTable,
                        columnGroups: action.columnGroups,
                    },
                },
            };

        case SubProductAction.TOGGLE_LOCALE_SHOWN:
            return {
                ...state,
                locale: state.locale === "en" ? "th" : "en",
            };

        case SubProductAction.TOGGLE_COLUMN_SHOWN:
            if (action.idx === undefined) return state;
            return {
                ...state,
                tables: {
                    ...state.tables,
                    [state.shownTableId]: {
                        ...stateCurrentTable,
                        columns: [
                            ...stateCurrentTable.columns.slice(0, action.idx),
                            {
                                ...stateCurrentTable.columns[action.idx],
                                isHidden:
                                    !stateCurrentTable.columns[action.idx]
                                        ?.isHidden,
                            },
                            ...stateCurrentTable.columns.slice(action.idx + 1),
                        ],
                    },
                },
            };

        case SubProductAction.TOGGLE_ROW_SHOWN:
            const subProduct =
                stateCurrentTable.subProducts[action.subProductId];
            return {
                ...state,
                tables: {
                    ...state.tables,
                    [state.shownTableId]: {
                        ...stateCurrentTable,
                        subProducts: {
                            ...stateCurrentTable.subProducts,
                            [action.subProductId]: {
                                ...subProduct,
                                isHidden: !subProduct.isHidden,
                            },
                        },
                    },
                },
            };

        case SubProductAction.OPEN_MODAL:
            // if the modal already exists, do nothing
            if (state.modals[action.modal.id]) return state;

            return {
                ...state,
                modalIds: [...state.modalIds, action.modal.id],
                modals: {
                    ...state.modals,
                    [action.modal.id]: action.modal,
                },
            };

        case SubProductAction.CLOSE_MODAL:
            return {
                ...state,
                modalIds: state.modalIds.filter((id) => id !== action.id),
                modals: {
                    ...state.modals,
                    [action.id]: undefined,
                },
            };

        default:
            return state;
    }
};

export default subProductsReducer;
